import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Download, Loader2, FileText, ClipboardList, Target, Calculator, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  feasibility: { label: 'Feasibility Report', icon: Calculator,    badge: 'bg-emerald-100 text-emerald-800' },
  cost:        { label: 'Cost Report',        icon: FileText,      badge: 'bg-amber-100 text-amber-800'   },
  brief:       { label: 'Brief',              icon: ClipboardList, badge: 'bg-sky-100 text-sky-800'       },
  fit:         { label: 'Fit / Readiness',    icon: Target,        badge: 'bg-violet-100 text-violet-800' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const fmtCurrency = (v) => {
  if (v == null || v === '') return '-';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(n);
};

const titleCase = (v) => (v || '').toString().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ============================================================
// Read-only snapshot renderers — one per artifact type. They
// receive the raw `data_snapshot` document and present what the
// client actually saw in the tool. No editing, no recalculation.
// ============================================================

function FeasibilitySnapshot({ snap }) {
  const cr = snap.cost_range || {};
  const asq = snap.affordable_sqm || {};
  return (
    <div className="space-y-4 text-sm font-body" data-testid="snap-feasibility">
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        <Field label="Location"        value={titleCase(snap.location)} />
        <Field label="Project Type"    value={titleCase(snap.project_type)} />
        <Field label="Building Type"   value={titleCase(snap.building_type) || '-'} />
        <Field label="Stand Size"      value={`${snap.stand_size || 0} m²`} />
        <Field label="Budget"          value={fmtCurrency(snap.budget)} />
        <Field label="Finish Level"    value={titleCase(snap.finish_level)} />
      </div>
      <div className="border-t border-black/10 pt-4">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/50 mb-2">Recommendation</p>
        <p className="font-heading text-lg text-brand-dark">{titleCase(snap.feasibility) || 'N/A'}</p>
        <p className="text-brand-dark/60 mt-1">
          Estimated cost range: <strong>{fmtCurrency(cr.min)} – {fmtCurrency(cr.max)}</strong>
        </p>
      </div>
      <div className="grid grid-cols-3 gap-4 border-t border-black/10 pt-4">
        <Field label="Buildable Area"     value={`${snap.buildable_area || 0} m²`} />
        <Field label="Affordable Build"   value={`${asq.min || 0} – ${asq.max || 0} m²`} />
        <Field label="Complexity"         value={`${snap.complexity || 0} / 10`} />
      </div>
    </div>
  );
}

function CostSnapshot({ snap }) {
  const tc = snap.total_cost || {};
  const cc = snap.construction_cost || {};
  const af = snap.architecture_fees || {};
  const ef = snap.engineer_fees || {};
  const ca = snap.council_approval || {};
  return (
    <div className="space-y-4 text-sm font-body" data-testid="snap-cost">
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        <Field label="Project Type"  value={titleCase(snap.project_type)} />
        <Field label="Building Type" value={titleCase(snap.building_type) || '-'} />
        <Field label="Size"          value={`${snap.size_sqm || 0} m²`} />
        <Field label="Finish Level"  value={titleCase(snap.finish_level)} />
        <Field label="Location"      value={titleCase(snap.location)} />
      </div>
      <div className="border-t border-black/10 pt-4">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/50 mb-2">Total Estimate</p>
        <p className="font-heading text-lg text-brand-accent-dark">
          {fmtCurrency(tc.min)} – {fmtCurrency(tc.max)}
        </p>
      </div>
      <div className="border-t border-black/10 pt-4 space-y-2">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/50 mb-1">Cost Breakdown</p>
        <Row label="Construction"        v={`${fmtCurrency(cc.min)} – ${fmtCurrency(cc.max)}`} />
        <Row label="Architecture Fees"   v={`${fmtCurrency(af.min)} – ${fmtCurrency(af.max)}`} />
        <Row label="Engineering"         v={`${fmtCurrency(ef.min)} – ${fmtCurrency(ef.max)}`} />
        <Row label="Council Approvals"   v={`${fmtCurrency(ca.min)} – ${fmtCurrency(ca.max)}`} />
      </div>
    </div>
  );
}

function BriefSnapshot({ snap }) {
  // Render every meaningful field the client filled in. Skip plumbing fields.
  const SKIP = new Set(['_id', 'brief_id', 'created_at', 'status', 'name', 'phone']);
  const entries = Object.entries(snap || {})
    .filter(([k, v]) => !SKIP.has(k) && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
    .filter(([k]) => k !== 'email');

  return (
    <div className="space-y-3 text-sm font-body" data-testid="snap-brief">
      {entries.length === 0 && <p className="text-brand-dark/40 italic">The brief is empty.</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        {entries.map(([k, v]) => (
          <div key={k}>
            <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/50 mb-0.5">
              {titleCase(k)}
            </p>
            <div className="text-brand-dark/80 break-words">
              {Array.isArray(v) ? (
                <div className="flex flex-wrap gap-1.5">
                  {v.map((item, i) => (
                    <Badge key={i} variant="outline" className="text-xs font-normal">
                      {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                    </Badge>
                  ))}
                </div>
              ) : typeof v === 'object' ? (
                <pre className="text-xs bg-brand-light/50 p-2 rounded-sm whitespace-pre-wrap">
                  {JSON.stringify(v, null, 2)}
                </pre>
              ) : (
                String(v)
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FitSnapshot({ snap }) {
  const total = snap.total_score ?? snap.score ?? 0;
  const tier = snap.tier || snap.recommendation || '';
  const breakdown = snap.breakdown || {};
  return (
    <div className="space-y-4 text-sm font-body" data-testid="snap-fit">
      <div className="text-center py-4 border-b border-black/10">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/50 mb-1">Project Fit Score</p>
        <p className="font-heading text-4xl text-brand-accent-dark">{total}<span className="text-lg text-brand-dark/40"> / 100</span></p>
        {tier && <p className="text-brand-dark/60 mt-1 capitalize">{titleCase(tier)}</p>}
      </div>
      {Object.keys(breakdown).length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/50">Breakdown</p>
          {Object.entries(breakdown).map(([k, v]) => (
            <Row key={k} label={titleCase(k)} v={typeof v === 'number' ? `${Math.round(v)}` : String(v)} />
          ))}
        </div>
      )}
      {snap.notes && (
        <div className="border-t border-black/10 pt-3">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/50 mb-1">Notes</p>
          <p className="text-brand-dark/70 whitespace-pre-wrap">{snap.notes}</p>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/50 mb-0.5">{label}</p>
    <p className="text-brand-dark/80">{value}</p>
  </div>
);

const Row = ({ label, v }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-dashed border-black/5 pb-1">
    <span className="text-brand-dark/60">{label}</span>
    <span className="font-medium text-brand-dark/80 text-right">{v}</span>
  </div>
);

const SNAPSHOT_RENDERERS = {
  feasibility: FeasibilitySnapshot,
  cost:        CostSnapshot,
  brief:       BriefSnapshot,
  fit:         FitSnapshot,
};

// ============================================================
// Preview modal — renders the inline snapshot for any type, and
// embeds the PDF inside an <iframe> for feasibility / cost.
// ============================================================
function ArtifactPreviewModal({ artifact, onClose }) {
  if (!artifact) return null;
  const meta = TYPE_META[artifact.type] || { label: artifact.type, icon: FileText };
  const Renderer = SNAPSHOT_RENDERERS[artifact.type];
  const pdfUrl = artifact.file_url ? `${API_URL}${artifact.file_url}` : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 overflow-y-auto"
         data-testid="artifact-preview-modal" onClick={onClose}>
      <div className="bg-white rounded-sm border border-black/10 max-w-4xl w-full my-8 max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-black/10 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <meta.icon className="w-5 h-5 text-brand-accent-dark" />
            <div>
              <h3 className="font-heading text-base font-medium text-brand-dark">{meta.label}</h3>
              <p className="text-xs font-body text-brand-dark/40">
                Created {fmtDate(artifact.created_at)} · Read-only client snapshot
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="artifact-modal-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Always show the structured snapshot for context, even when a PDF exists */}
          {Renderer && artifact.data_snapshot && (
            <div className="bg-brand-light/40 rounded-sm p-5 border border-black/5"
                 data-testid="artifact-snapshot">
              <Renderer snap={artifact.data_snapshot} />
            </div>
          )}

          {/* Embedded PDF preview */}
          {pdfUrl && (
            <div className="border border-black/10 rounded-sm overflow-hidden" data-testid="artifact-pdf-frame">
              <iframe
                src={pdfUrl}
                title={meta.label}
                className="w-full"
                style={{ height: '70vh' }}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-black/10 bg-brand-light/30 sticky bottom-0">
          {pdfUrl && (
            <a href={pdfUrl} download target="_blank" rel="noreferrer" data-testid="artifact-modal-download">
              <Button size="sm" variant="outline" className="rounded-sm font-body text-xs uppercase tracking-wider">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download PDF
              </Button>
            </a>
          )}
          <Button size="sm" onClick={onClose}
            className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Top-level: Lead Artifacts panel — slots into the Lead detail
// page, replacing the legacy "Linked Reports" id-only listing.
// ============================================================
export default function LeadArtifacts({ leadId }) {
  const [artifacts, setArtifacts] = useState(null);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setArtifacts(null);
    setError(null);
    api.get(`/leads/${leadId}/artifacts`)
      .then(r => { if (!cancelled) setArtifacts(r.data || []); })
      .catch(() => { if (!cancelled) { setArtifacts([]); setError('Failed to load artifacts'); } });
    return () => { cancelled = true; };
  }, [leadId]);

  const downloadPdf = (artifact) => {
    if (!artifact.file_url) return;
    window.open(`${API_URL}${artifact.file_url}`, '_blank', 'noopener,noreferrer');
  };

  if (artifacts === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-brand-dark/50 font-body py-3">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading client artifacts...
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-red-500 font-body">{error}</p>;
  }

  if (artifacts.length === 0) {
    return (
      <p className="text-brand-dark/30 text-sm font-body" data-testid="lead-artifacts-empty">
        No client artifacts captured yet.
      </p>
    );
  }

  return (
    <div data-testid="lead-artifacts">
      <div className="space-y-2">
        {artifacts.map((a) => {
          const meta = TYPE_META[a.type] || { label: a.type, icon: FileText, badge: 'bg-gray-100 text-gray-700' };
          const Icon = meta.icon;
          return (
            <div key={a.id}
              className="flex items-center gap-3 py-2.5 px-3 bg-brand-light/50 rounded-sm border border-black/5"
              data-testid={`lead-artifact-${a.id}`}>
              <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${meta.badge}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-body text-sm text-brand-dark font-medium">{meta.label}</p>
                <p className="text-xs text-brand-dark/40 font-body">{fmtDate(a.created_at)}</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button size="sm" variant="ghost" onClick={() => setActive(a)}
                  data-testid={`artifact-preview-${a.id}`}
                  className="h-8 px-2.5 text-xs font-body uppercase tracking-wider text-brand-dark/70 hover:text-brand-dark">
                  <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                </Button>
                {a.file_url && (
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(a)}
                    data-testid={`artifact-download-${a.id}`}
                    className="h-8 px-2.5 text-xs font-body uppercase tracking-wider text-brand-dark/70 hover:text-brand-dark">
                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ArtifactPreviewModal artifact={active} onClose={() => setActive(null)} />
    </div>
  );
}
